import { useState, useEffect } from 'react'
import { MerchantLayout } from '../../components/layouts/Layout'
import { useAuth } from '../../hooks/useAuth'
import { Card, Button, Badge, Modal } from '../../components/common/UI'
import { User, Mail, Phone, Shield, Edit, ShieldCheck, ShieldAlert } from 'lucide-react'
import { merchantService } from '../../services/api'

export default function Profile() {
  const { user } = useAuth()
  const [merchant, setMerchant] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [verifyModal, setVerifyModal] = useState<{ open: boolean; type: 'email' | 'phone' | null; value: string }>({ open: false, type: null, value: '' })
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [error, setError] = useState('')
  const [verifyLoading, setVerifyLoading] = useState(false)

  useEffect(() => {
    loadMerchantData()
  }, [user?.merchantId])

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const loadMerchantData = async () => {
    if (!user?.merchantId) return
    
    try {
      const data = await merchantService.get(user.merchantId)
      setMerchant(data)
    } catch (error) {
      console.error('Failed to load merchant:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenVerify = (type: 'email' | 'phone', value: string) => {
    setVerifyModal({ open: true, type, value })
    setOtp('')
    setOtpSent(false)
    setError('')
    setCountdown(0)
  }

  const handleSendOTP = async () => {
    setVerifyLoading(true)
    setError('')
    
    try {
      await merchantService.sendOTP(verifyModal.value)
      setOtpSent(true)
      setCountdown(60)
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP')
    } finally {
      setVerifyLoading(false)
    }
  }

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      setError('Please enter a 6-digit OTP')
      return
    }

    setVerifyLoading(true)
    setError('')

    try {
      const isValid = await merchantService.verifyOTP(verifyModal.value, otp)
      
      if (isValid && user?.merchantId && verifyModal.type) {
        await merchantService.updateVerification(user.merchantId, verifyModal.type)
        setVerifyModal({ open: false, type: null, value: '' })
        setOtp('')
        setOtpSent(false)
        await loadMerchantData()
      } else {
        setError('Invalid OTP. Please try again.')
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed')
    } finally {
      setVerifyLoading(false)
    }
  }

  if (loading) {
    return (
      <MerchantLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p>Loading...</p>
        </div>
      </MerchantLayout>
    )
  }

  return (
    <MerchantLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Profile Settings</h1>
            <p className="text-gray-600">Manage your account information</p>
          </div>
        </div>

        <Card>
          <div className="flex items-center space-x-3 mb-6">
            <Shield className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold">Account Security</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium">Email Address</p>
                  <p className="text-sm text-gray-600">{merchant?.email}</p>
                </div>
              </div>
              {merchant?.emailVerified ? (
                <Badge variant="success" className="flex items-center space-x-1">
                  <ShieldCheck className="w-3 h-3" />
                  <span>Verified</span>
                </Badge>
              ) : (
                merchant?.email && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleOpenVerify('email', merchant.email)}
                  >
                    Verify Email
                  </Button>
                )
              )}
            </div>

            {merchant?.phoneNumber && (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium">Phone Number</p>
                    <p className="text-sm text-gray-600">{merchant.phoneNumber}</p>
                  </div>
                </div>
                {merchant.phoneVerified ? (
                  <Badge variant="success" className="flex items-center space-x-1">
                    <ShieldCheck className="w-3 h-3" />
                    <span>Verified</span>
                  </Badge>
                ) : (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleOpenVerify('phone', merchant.phoneNumber)}
                  >
                    Verify Phone
                  </Button>
                )}
              </div>
            )}

            {(!merchant?.emailVerified || (merchant?.phoneNumber && !merchant?.phoneVerified)) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-blue-900 font-medium mb-2">
                  Why verify your account?
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>✓ Required to create business profiles</li>
                  <li>✓ Enhanced account security</li>
                  <li>✓ Receive important notifications</li>
                  <li>✓ Higher trust with customers</li>
                </ul>
              </div>
            )}
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3 mb-6">
            <User className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold">Personal Information</h2>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <p className="text-gray-900">{merchant?.fullName}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Status
                </label>
                <Badge variant={
                  merchant?.accountStatus === 'ACTIVE' ? 'success' :
                  merchant?.accountStatus === 'SUSPENDED' ? 'error' : 'warning'
                }>
                  {merchant?.accountStatus}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credit Balance
                </label>
                <p className="text-gray-900 font-semibold">
                  {merchant?.creditBalance || 0} credits
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Spent
                </label>
                <p className="text-gray-900 font-semibold">
                  ${merchant?.totalSpent?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold mb-4">Account Actions</h2>
          <div className="space-y-3">
            <Button variant="outline" className="w-full justify-start" icon={Edit}>
              Change Password
            </Button>
          </div>
        </Card>

        <Modal
          isOpen={verifyModal.open}
          onClose={() => setVerifyModal({ open: false, type: null, value: '' })}
          title={`Verify ${verifyModal.type === 'email' ? 'Email' : 'Phone Number'}`}
          size="md"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
              {verifyModal.type === 'email' ? (
                <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              ) : (
                <Phone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className="font-medium text-blue-900">
                  {verifyModal.type === 'email' ? 'Email' : 'Phone'}: {verifyModal.value}
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  We'll send a 6-digit verification code to this {verifyModal.type === 'email' ? 'email address' : 'phone number'}.
                </p>
              </div>
            </div>

            {!otpSent ? (
              <Button onClick={handleSendOTP} loading={verifyLoading} className="w-full">
                Send Verification Code
              </Button>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Enter 6-digit code
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '')
                      setOtp(value)
                      setError('')
                    }}
                    placeholder="000000"
                    className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                    <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div className="flex space-x-3">
                  <Button
                    onClick={handleVerifyOTP}
                    loading={verifyLoading}
                    disabled={otp.length !== 6}
                    className="flex-1"
                  >
                    Verify Code
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={handleSendOTP}
                    disabled={countdown > 0 || verifyLoading}
                    className="flex-1"
                  >
                    {countdown > 0 ? `Resend (${countdown}s)` : 'Resend Code'}
                  </Button>
                </div>

                <p className="text-xs text-gray-500 text-center">
                  Didn't receive the code? Check your spam folder or try resending.
                </p>
              </>
            )}
          </div>
        </Modal>
      </div>
    </MerchantLayout>
  )
}